export const PNG_EXPORT_MAX_PIXELS = 8_500_000;

export interface ResolveHtml2CanvasScaleInput {
  readonly cssWidth: number;
  readonly cssHeight: number;
  /** Pixel budget for a single canvas (width×height). */
  readonly maxPixels?: number;
  /** Device pixel ratio; preferred scale is `Math.min(dpr, 2)`. */
  readonly devicePixelRatio?: number;
  /** Floor so capture remains usable when budget is extremely tight. */
  readonly minScale?: number;
}

/**
 * Conservative canvas pixel estimate (ceil) so WebKit overrun hardening never
 * plans a canvas that rounds up over {@link PNG_EXPORT_MAX_PIXELS}.
 * Defensive budget math — not a proven live Safari/device root-cause claim.
 */
export function estimateExportCanvasPixels(
  cssWidth: number,
  cssHeight: number,
  scale: number,
): number {
  const width = Math.max(1, Math.ceil(cssWidth * scale));
  const height = Math.max(1, Math.ceil(cssHeight * scale));
  return width * height;
}

/**
 * Spec §6.6 / G39: `scale` 2 only if pixel budget allows; else lower
 * (`Math.min(dpr, 2)` capped by budget). Never hardcode scale: 2.
 */
export function resolveHtml2CanvasScale(input: ResolveHtml2CanvasScaleInput): number {
  const maxPixels = input.maxPixels ?? PNG_EXPORT_MAX_PIXELS;
  const dpr =
    input.devicePixelRatio ??
    (typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? window.devicePixelRatio
      : 1);
  const preferred = Math.min(2, Math.max(1, dpr || 1));
  const minScale = input.minScale ?? 0.5;
  const width = Math.max(1, input.cssWidth);
  const height = Math.max(1, input.cssHeight);
  const area = width * height;
  const budgetScale = Math.sqrt(maxPixels / area);
  if (!Number.isFinite(budgetScale) || budgetScale <= 0) {
    return minScale;
  }
  return Math.max(minScale, Math.min(preferred, budgetScale));
}

/**
 * Clamp scale so a 1-CSS-px-tall vertical band stays within budget.
 * WebKit overrun hardening for width-dominant pages where vertical banding alone cannot help.
 * Defensive — not attributed to a live Safari/device reproduction in-repo.
 */
export function clampScaleToBandableExportWidth(
  cssWidth: number,
  scale: number,
  maxPixels: number = PNG_EXPORT_MAX_PIXELS,
  minScale: number = 0.5,
): number {
  if (!isExportPixelBudgetExceeded(cssWidth, 1, scale, maxPixels)) {
    return scale;
  }
  let lo = minScale;
  let hi = scale;
  let best = minScale;
  for (let i = 0; i < 32; i += 1) {
    const mid = (lo + hi) / 2;
    if (!isExportPixelBudgetExceeded(cssWidth, 1, mid, maxPixels)) {
      best = mid;
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return best;
}

/** True when even preferred min-capped scale would exceed the pixel budget. */
export function isExportPixelBudgetExceeded(
  cssWidth: number,
  cssHeight: number,
  scale: number,
  maxPixels: number = PNG_EXPORT_MAX_PIXELS,
): boolean {
  return estimateExportCanvasPixels(cssWidth, cssHeight, scale) > maxPixels;
}

/**
 * Max CSS band height whose raster stays within `maxPixels`.
 * Used when a full-page capture would allocate one over-budget canvas.
 * Returns the largest height ≥ 1 that fits; callers must re-check when width alone exceeds.
 */
export function resolveExportCaptureBandCssHeight(
  cssWidth: number,
  scale: number,
  maxPixels: number = PNG_EXPORT_MAX_PIXELS,
): number {
  const width = Math.max(1, cssWidth);
  const safeScale = Math.max(scale, Number.EPSILON);
  let cssHeight = Math.max(1, Math.floor(maxPixels / (width * safeScale * safeScale)));

  while (cssHeight > 1 && isExportPixelBudgetExceeded(width, cssHeight, scale, maxPixels)) {
    cssHeight -= 1;
  }
  return cssHeight;
}

/** Max device-pixel band height for an already-rasterized canvas width. */
export function resolveExportCaptureBandDeviceHeight(
  canvasWidth: number,
  maxPixels: number = PNG_EXPORT_MAX_PIXELS,
): number {
  return Math.max(1, Math.floor(maxPixels / Math.max(1, canvasWidth)));
}

/** True when a live canvas stays within the export pixel budget. */
export function isCanvasWithinExportPixelBudget(
  canvas: Pick<HTMLCanvasElement, 'width' | 'height'>,
  maxPixels: number = PNG_EXPORT_MAX_PIXELS,
): boolean {
  return canvas.width * canvas.height <= maxPixels;
}
